<template>
  <b-container fluid class="py-4">
    <!-- Header and Search -->
    <b-card no-body class="mb-4 shadow-sm rounded-lg">
      <b-card-header class="bg-white border-0 d-flex justify-content-between align-items-center py-3">
        <h3 class="mb-0 font-weight-bold text-dark">Pengurusan Pengguna</h3>
        <b-button variant="primary" class="px-4 py-2 rounded-pill" v-b-modal.modal-tambah-pengguna>
          <i class="fas fa-plus mr-2"></i> Tambah Pengguna
        </b-button>
      </b-card-header>
      <!-- Search Filter Section -->
      <b-card-body class="py-3">
        <b-row>
          <b-col md="6">
            <b-form-group label="Carian No Kad Pengenalan" label-class="font-weight-bold text-muted">
              <b-input-group>
                <b-form-input id="nokp-search" v-model="searchNokp" type="text" placeholder="Masukkan No Kad Pengenalan"
                  maxlength="12" pattern="\d*" class="rounded-pill" @keyup.enter="handleSearch" @input="validateNokp"
                  oninput="this.value = this.value.replace(/[^0-9]/g, '').slice(0, 12)"></b-form-input>
                <b-input-group-append>
                  <b-button variant="primary" class="rounded-pill ml-2" @click="handleSearch"
                    :disabled="searchNokp.length !== 12">
                    <i class="fas fa-search mr-1"></i> Cari
                  </b-button>
                  <b-button variant="outline-secondary" class="rounded-pill ml-2" @click="resetSearch"
                    v-if="searchNokp">
                    <i class="fas fa-times mr-1"></i> Set Semula
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <small v-if="searchNokp.length > 0 && searchNokp.length !== 12" class="text-danger">
                No Kad Pengenalan mesti 12 digit dan hanya nombor sahaja
              </small>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
    <!-- Modal untuk Tambah Pengguna -->
    <b-modal id="modal-tambah-pengguna" ref="modalTambahPengguna" title="Tambah Pengguna Baharu"
      @show="resetTambahPengguna" hide-footer header-class="bg-white border-0 py-3" body-class="p-4"
      header-bg-variant="white" header-text-variant="dark" size="lg">
      <form ref="formTambahPengguna" @submit.stop.prevent="handleTambahPenggunaSubmit">
        <!-- Search Section - Hidden when registration form is shown -->
        <div v-if="!showRegistrationForm" class="search-section mb-4">
          <h5 class="font-weight-bold text-dark mb-3">
            <i class="fas fa-search mr-2 text-primary"></i>Cari Maklumat Pengguna
          </h5>
          <b-form-group label="No Kad Pengenalan" label-class="font-weight-bold text-muted">
            <b-input-group>
              <b-form-input id="nokp-cari-input" v-model="nokp" type="text" placeholder="Contoh: 790101015678"
                maxlength="12" pattern="\d*" class="rounded-left-pill form-control-modern"
                oninput="this.value = this.value.replace(/[^0-9]/g, '').slice(0, 12)">
              </b-form-input>
              <b-input-group-append>
                <b-button variant="primary" class="rounded-right-pill px-4" @click="cariPengguna">
                  <i class="fas fa-search mr-1"></i> Cari
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <small v-if="nokp.length > 0 && nokp.length !== 12" class="text-danger">
              <i class="fas fa-exclamation-circle mr-1"></i>No Kad Pengenalan mesti 12 digit angka
            </small>
            <small v-if="nonHrmisData.nokp.length === 12 && !validateAgeFromNokp(nonHrmisData.nokp)"
              class="text-danger">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              Pengguna berumur kurang daripada 17 tahun. Pendaftaran tidak dibenarkan.
            </small>
          </b-form-group>
        </div>
        <!-- HRMIS User Info Section -->
        <div v-if="!showRegistrationForm && carianPengguna && carianPengguna.name !== 'Tiada Maklumat Pengguna'"
          class="user-info-section">
          <h5 class="font-weight-bold text-dark mb-3">
            <i class="fas fa-user-circle mr-2 text-primary"></i>Maklumat Pengguna
          </h5>
          <b-card class="shadow-sm rounded-lg border-0 mb-4"
            :class="{ 'border-success': carianPengguna.ada_record === 0, 'border-warning': carianPengguna.ada_record === 1 }">
            <div class="user-details">
              <div class="detail-item">
                <span class="detail-label">Nama:</span>
                <span class="detail-value font-weight-bold">{{ carianPengguna.name }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Emel:</span>
                <span class="detail-value">{{ carianPengguna.email }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ carianPengguna.agency_id === 'Tiada Maklumat Organisasi' ? 'Agensi:' :
                  'Organisasi:' }}</span>
                <span class="detail-value">{{ carianPengguna.ministry }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">No Kad Pengenalan:</span>
                <span class="detail-value">{{ carianPengguna.nokp }}</span>
              </div>
              <div v-if="carianPengguna.ada_record === 1" class="detail-item">
                <span class="detail-label">Status:</span>
                <b-badge :variant="carianPengguna.user_status === 'active' ? 'success' : 'danger'" pill
                  class="px-3 py-1">
                  {{ carianPengguna.user_status === 'active' ? 'Aktif' : 'Tidak Aktif' }}
                </b-badge>
              </div>
            </div>
          </b-card>
          <!-- Dalam action-buttons, tambah kondisi untuk sembunyikan butang jika akses ditolak -->
          <div class="action-buttons text-center" v-if="carianPengguna && carianPengguna.name !== 'Akses Ditolak'">
            <b-button variant="success" class="rounded-pill px-4 py-2 mr-3"
              :disabled="isUserUnder17 || carianPengguna.ada_record === 1" v-if="carianPengguna
                && carianPengguna.name !== 'Tiada Maklumat Pengguna'
                && carianPengguna.name !== 'Akses Ditolak'
                && carianPengguna.ada_record === 0" @click="tambahPengguna" v-b-tooltip.hover="{
          title: isUserUnder17 ? 'Pengguna berumur kurang daripada 17 tahun. Pendaftaran tidak dibenarkan.' : '',
          disabled: !isUserUnder17
        }">
              <i class="fas fa-user-plus mr-2"></i> Tambah Pengguna
            </b-button>
            <b-button variant="warning" class="rounded-pill px-4 py-2"
              v-if="carianPengguna.ada_record === 1 && carianPengguna.user_status === 'inactive'"
              @click="aktifkanPengguna">
              <i class="fas fa-user-check mr-2"></i> Aktifkan Semula
            </b-button>
          </div>
        </div>
        <div v-if="carianPengguna && carianPengguna.name === 'Akses Ditolak'" class="access-denied-section">
          <div class="access-denied text-center py-4 mb-4">
            <i class="fas fa-ban fa-3x text-danger mb-3"></i>
            <h5 class="text-danger font-weight-bold">Akses Ditolak</h5>
            <p class="text-muted">{{ carianPengguna.message || 'Anda tidak dibenarkan mendaftar pengguna dari organisasi/agensi yang berbeza.' }}</p>
            <b-alert variant="warning" show class="small mt-3">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              <strong>Peranan Pentadbir Organisasi:</strong> Hanya boleh mendaftar pengguna dari agensi yang sama.
            </b-alert>
          </div>
        </div>

        <!-- User Not Found Section - Paparan untuk Admin & Pentadbir Awam -->
        <div v-if="showPublicRegistration && !showRegistrationForm && isAdmin" class="public-registration-section">
          <div class="no-user-found text-center py-4 mb-4">
            <i class="fas fa-user-slash fa-3x text-muted mb-3"></i>
            <h5 class="text-dark font-weight-bold">Pengguna Tidak Dijumpai</h5>
            <p class="text-muted">Tiada rekod pengguna ditemukan untuk No Kad Pengenalan ini.</p>
            <b-button variant="outline-primary" class="rounded-pill mt-2" @click="togglePublicRegistration">
              <i class="fas fa-user-plus mr-2"></i> Daftar Pengguna Awam
            </b-button>
          </div>
        </div>
        <div v-if="showPublicRegistration && !showRegistrationForm && !isAdmin" class="public-registration-section">
          <div class="no-user-found text-center py-4 mb-4">
            <i class="fas fa-user-slash fa-3x text-muted mb-3"></i>
            <h5 class="text-dark font-weight-bold">Pengguna Tidak Dijumpai</h5>
            <p class="text-muted">Tiada rekod pengguna ditemukan dalam sistem HRMIS untuk No Kad Pengenalan ini.</p>
            <b-alert variant="info" show class="small mt-3">
              <i class="fas fa-info-circle mr-2"></i>
              Sila pastikan No Kad Pengenalan yang dimasukkan adalah betul dan pengguna tersebut berada dalam
              organisasi yang sama.
            </b-alert>
          </div>
        </div>
        <!-- Public Registration Form -->
        <div v-if="showRegistrationForm" class="registration-form-section">
          <h5 class="font-weight-bold text-dark mb-4">
            <i class="fas fa-user-plus mr-2 text-primary"></i>Pendaftaran Pengguna Awam
          </h5>
          <b-form @submit.stop.prevent="tambahPenggunaNonHRMIS">
            <b-row>
              <b-col md="6">
                <b-form-group label="Nama Penuh" label-for="name-input" class="mb-4 input-group-animated">
                  <b-form-input id="name-input" v-model="nonHrmisData.name" placeholder="Masukkan nama penuh" required
                    class="form-control-animated text-uppercase" style="text-transform: uppercase;"
                    @input="nonHrmisData.name = $event.toUpperCase()"></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="No Kad Pengenalan" label-for="nokp-input" class="mb-4 input-group-animated">
                  <b-form-input id="nokp-input" v-model="nonHrmisData.nokp"
                    :placeholder="nokp ? nokp : 'Contoh: 790101015678'" maxlength="12" pattern="\d*" required
                    class="form-control-animated" readonly
                    oninput="this.value = this.value.replace(/[^0-9]/g, '').slice(0, 12)"></b-form-input>
                  <small v-if="nonHrmisData.nokp.length > 0 && nonHrmisData.nokp.length !== 12" class="text-danger">
                    No Kad Pengenalan mesti 12 digit angka
                  </small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12">
                <b-form-group label="Agensi" class="mb-4">
                  <!-- Hide agency toggle buttons for 'pentadbir awam' role -->
                  <div v-if="!isPublicAdmin" class="agency-toggle mb-3">
                    <div class="agency-card" :class="{ active: agencyMode === 'existing' }"
                      @click="setAgencyMode('existing')">
                      <i class="fas fa-building mr-2"></i> Pilih Agensi Sedia Ada
                    </div>
                    <div class="agency-card" :class="{ active: agencyMode === 'new' }" @click="setAgencyMode('new')">
                      <i class="fas fa-plus-circle mr-2"></i> Tambah Agensi Baharu
                    </div>
                  </div>
                  <transition name="fade-slide" mode="out-in">
                    <!-- For 'pentadbir awam' role, show only existing agency selection -->
                    <div v-if="isPublicAdmin || agencyMode === 'existing'" class="agency-input-wrapper" key="existing">
                      <b-form-select v-model="nonHrmisData.buid" :options="agencyOptions" :disabled="isPublicAdmin"
                        class="form-control-animated white-bg" :class="{ 'disabled-white-bg': isPublicAdmin }"
                        @change="handleAgencySelection">
                        <template #first>
                          <option :value="null" disabled>Sila pilih agensi</option>
                        </template>
                      </b-form-select>
                      <b-tooltip target="existing-agency-select" triggers="hover">
                        {{ isPublicAdmin ? 'Agensi telah ditetapkan untuk peranan pentadbir organisasi' : 'Pilih daripada senarai agensi yang telah didaftarkan.' }}
                      </b-tooltip>
                    </div>
                    <div v-else-if="!isPublicAdmin && agencyMode === 'new'" class="agency-input-wrapper" key="new">
                      <b-form-input v-model="nonHrmisData.ministry" placeholder="Masukan nama agensi baharu"
                        maxlength="50" class="form-control-animated white-bg text-uppercase"
                        style="text-transform: uppercase;" @input="handleAgencyInput"
                        @change="nonHrmisData.buid = null"></b-form-input>
                      <b-tooltip target="new-agency-input" triggers="hover">
                        Taip nama agensi baharu untuk didaftarkan dalam sistem.
                      </b-tooltip>
                    </div>
                  </transition>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <b-form-group label="Alamat Emel" label-for="email-input" class="mb-4 input-group-animated">
                  <b-form-input id="email-input" v-model="nonHrmisData.email" type="email"
                    placeholder="contoh@email.com" required class="form-control-animated"></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Peranan" label-for="role-select" class="mb-4 input-group-animated">
                  <b-form-select id="role-select" v-model="nonHrmisData.role" :options="roleOptions" required
                    class="form-control-animated white-bg">
                    <template #first>
                      <option :value="null" disabled>Sila pilih peranan</option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </b-col>
            </b-row>
            <div class="action-buttons text-center mt-4 pt-3 border-top">
              <b-button variant="outline-secondary" class="rounded-pill px-4 py-2 mr-3"
                @click="cancelPublicRegistration">
                <i class="fas fa-times mr-2"></i> Batal
              </b-button>
              <b-button variant="primary" type="submit" class="rounded-pill px-4 py-2" :disabled="!isNonHrmisFormValid"
                v-b-tooltip.hover="{
                  title: !validateAgeFromNokp(nonHrmisData.nokp) ? 'Umur mesti 17 tahun ke atas' : '',
                  disabled: validateAgeFromNokp(nonHrmisData.nokp)
                }">
                <i class="fas fa-user-plus mr-2"></i> Daftar Pengguna
              </b-button>
            </div>
          </b-form>
        </div>
      </form>
    </b-modal>
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5 loading-container">
      <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem;"></b-spinner>
      <p class="mt-3 text-muted font-weight-medium">Memuatkan data...</p>
    </div>
    <!-- User Table -->
    <div v-else-if="paginatedUsers.length > 0" class="table-container">
      <b-table :items="paginatedUsers" :fields="visibleFields" responsive hover striped bordered
        class="user-table rounded-lg shadow-sm">
        <template #cell(name)="data">
          <span class="font-weight-medium">{{ data.item.name }}</span>
        </template>
        <template #cell(email)="data">
          <span class="text-dark">{{ data.item.email }}</span>
        </template>
        <template #cell(ministry)="data">
          <span class="text-dark">{{ data.item.ministry }}</span>
        </template>
        <template #cell(status_entitle)="data">
          <b-badge variant="primary" class="px-3 py-2 rounded-pil
l">
            {{ data.item.status_entitle }}
          </b-badge>
        </template>
        <template #cell(icno)="data">
          <span class="text-dark">{{ data.item.icno }}</span>
        </template>
        <template #cell(actions)="data">
          <b-button variant="outline-primary" size="sm" class="rounded-pill mr-2"
            @click="navigateToUser(data.item.user_id)">
            <i class="fas fa-eye mr-1"></i> Papar
          </b-button>
          <b-button variant="outline-warning" size="sm" class="rounded-pill mr-2" @click="editUser(data.item.user_id)">
            <i class="fas fa-pencil-alt mr-1"></i> Kemaskini
          </b-button>
          <b-button variant="outline-info" size="sm" class="rounded-pill" @click="tukarMilikProjek(data.item.user_id)">
            <i class="fas fa-arrows-alt-h mr-1"></i> Tukar Milik
          </b-button>
        </template>
      </b-table>
      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center mt-4">
        <div class="pagination-info">
          <strong>Papar {{ startIndex + 1 }} - {{ endIndex }} daripada {{ totalUsers }} pengguna</strong>
        </div>
        <b-pagination v-model="currentPage" :total-rows="totalUsers" :per-page="perPage" pills
          @change="handlePageChange"></b-pagination>
      </div>
    </div>
    <!-- No Users -->
    <div v-else class="no-data-container text-center py-5">
      <i class="fas fa-users fa-3x text-muted mb-3"></i>
      <h4 class="text-dark font-weight-bold">Tiada Pengguna</h4>
      <p class="text-muted mb-4">Sila tambah pengguna baharu untuk memulakan.</p>
      <b-button variant="primary" class="px-4 py-2 rounded-pill" v-b-modal.modal-tambah-pengguna>
        <i class="fas fa-plus mr-2"></i> Tambah Pengguna
      </b-button>
    </div>
  </b-container>
</template>
<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseURL } from '../../../services/baseURL';
export default {
  name: 'UserManagement',
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      listuser: [],
      loading: true,
      nokp: '',
      searchNokp: '',
      carianPengguna: null,
      nameTambah: '',
      emailTambah: '',
      ministryTambah: '',
      isSearching: false,
      showPublicRegistration: false,
      showRegistrationForm: false,
      agencyOptions: [],
      agencyMode: 'existing', // Default to selecting existing agency
      agencyModeOptions: [
        { text: 'Pilih Agensi Sedia Ada', value: 'existing' },
        { text: 'Tambah Agensi Baharu', value: 'new' }
      ],
      nonHrmisData: {
        name: '',
        nokp: '',
        email: '',
        ministry: '',
        buid: null,
        role: null,
        phone: ''
      },
      roleOptions: [
        { value: 'pengguna', text: 'Pengguna' },
        { value: 'pentadbir awam', text: 'Pentadbir Organisasi' }
      ],
    };
  },
  computed: {
    isUserUnder17() {
      return this.nokp.length === 12 && !this.validateAgeFromNokp(this.nokp);
    },
    filteredAgencyOptions() {
      if (this.isPublicAdmin) {
        // For pentadbir awam, filter to show only their agency
        const user = JSON.parse(localStorage.getItem('user'));
        const userBuid = user ? user.buidagensi : null;
        if (userBuid) {
          return this.agencyOptions.filter(agency => agency.value == userBuid);
        }
        return [];
      }
      // For admin, show all agencies
      return this.agencyOptions;
    },
    isAdmin() {
      const user = JSON.parse(localStorage.getItem('user'));
      return user && user.peranan &&
        (user.peranan.toLowerCase() === 'admin' ||
          user.peranan.toLowerCase() === 'pentadbir awam');
    },
    isSystemAdmin() {
      const user = JSON.parse(localStorage.getItem('user'));
      return user && user.peranan && user.peranan.toLowerCase() === 'admin';
    },
    isPublicAdmin() {
      const user = JSON.parse(localStorage.getItem('user'));
      return user && user.peranan && user.peranan.toLowerCase() === 'pentadbir awam';
    },
    userRole() {
      const user = JSON.parse(localStorage.getItem('user'));
      return user ? user.peranan : null;
    },
    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },
    endIndex() {
      const end = this.currentPage * this.perPage;
      return Math.min(end, this.totalUsers);
    },
    userPeranan() {
      const user = JSON.parse(localStorage.getItem('user'));
      return user.peranan || user.buidagensi || 'Tiada Peranan';
    },
    showIcnoColumn() {
      return this.isSearching && this.searchNokp.length === 12;
    },
    visibleFields() {
      const fields = [
        { key: 'name', label: 'Nama', sortable: true, class: 'align-middle' },
        { key: 'email', label: 'Emel', sortable: true, class: 'align-middle' },
        {
          key: 'ministry',
          label: this.userRole === 'pentadbir awam' ? 'Agensi' : 'Organisasi',
          sortable: true,
          class: 'align-middle'
        },
        { key: 'status_entitle', label: 'Peranan', sortable: true, class: 'align-middle text-center' },
        { key: 'icno', label: 'No Kad Pengenalan', sortable: true, class: 'align-middle', showWhenSearching: true },
        { key: 'actions', label: 'Tindakan', class: 'align-middle text-center' },
      ];
      return fields.filter(field => !field.showWhenSearching || this.showIcnoColumn);
    },
    filteredUsers() {
      if (this.isSearching && this.searchNokp.length === 12) {
        return this.listuser.filter(user => user.icno && user.icno.includes(this.searchNokp));
      }
      return this.listuser;
    },
    totalUsers() {
      return this.filteredUsers.length;
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredUsers.slice(start, end);
    },
    isNonHrmisFormValid() {
      if (!this.isAdmin) return false;
      const validNokp = this.nonHrmisData.nokp.length === 12 && this.validateAgeFromNokp(this.nonHrmisData.nokp);
      return (
        this.nonHrmisData.name.trim() &&
        validNokp &&
        this.nonHrmisData.email.trim() &&
        (this.nonHrmisData.buid || this.nonHrmisData.ministry.trim()) &&
        this.nonHrmisData.role
      );
    }
  },
  methods: {
    validateAgeFromNokp(nokp) {
      if (!nokp || nokp.length !== 12 || !/^\d+$/.test(nokp)) return false;

      const yearPrefix = parseInt(nokp.substring(0, 2)); // Dua digit pertama
      const currentYear = new Date().getFullYear() % 100; // 25 untuk 2025
      const birthYear = yearPrefix > currentYear ? 1900 + yearPrefix : 2000 + yearPrefix;

      const birthDate = new Date(
        birthYear,
        parseInt(nokp.substring(2, 4)) - 1, // bulan (0-indexed)
        parseInt(nokp.substring(4, 6))  // hari
      );

      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age >= 17;
    },
    isSameAgency(userAgencyId) {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser || !currentUser.buidagensi) return false;
      return currentUser.buidagensi == userAgencyId;
    },
    showAccessDeniedPopup() {
      Swal.fire({
        title: 'Akses Ditolak',
        html: `Anda tidak dibenarkan mendaftar pengguna dari organisasi/agensi yang berbeza.<br><br>
           <strong>Peranan Pentadbir Organisasi:</strong> Hanya boleh mendaftar pengguna dari agensi yang sama.`,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    },
    async fetchAgencies() {
      try {
        const response = await axios.get(`${baseURL}/fetch_agencies.php`);
        if (response.data.success) {
          this.agencyOptions = response.data.data.map(agency => ({
            value: agency.buid,
            text: agency.NamaBU
          }));
          // Auto-select agency for pentadbir awam
          if (this.isPublicAdmin) {
            const user = JSON.parse(localStorage.getItem('user'));
            const userBuid = user ? user.buidagensi : null;
            if (userBuid) {
              this.nonHrmisData.buid = parseInt(userBuid);
              const selectedAgency = this.agencyOptions.find(agency => agency.value == userBuid);
              if (selectedAgency) {
                this.nonHrmisData.ministry = selectedAgency.text;
              }
            }
          }
        } else {
          Swal.fire({
            title: 'Ralat',
            text: 'Gagal memuat senarai agensi.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
      } catch (error) {
        Swal.fire({
          title: 'Ralat',
          text: 'Ralat pelayan semasa memuat senarai agensi.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    },
    handleAgencySelection(buid) {
      if (buid) {
        const selectedAgency = this.agencyOptions.find(agency => agency.value === buid);
        this.nonHrmisData.ministry = selectedAgency ? selectedAgency.text : '';
      } else {
        this.nonHrmisData.ministry = '';
      }
    },
    setAgencyMode(mode) {
      this.agencyMode = mode;
      if (mode === 'existing') {
        this.nonHrmisData.ministry = '';
      } else {
        this.nonHrmisData.buid = null;
      }
    },
    async tambahPenggunaNonHRMIS() {
      if (!this.isAdmin) {
        Swal.fire({
          title: 'Akses Ditolak',
          text: 'Hanya pentadbir sistem dan pentadbir organisasi yang boleh menambah pengguna organisasi.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
        return;
      }
      if (!this.validateAgeFromNokp(this.nonHrmisData.nokp)) {
        Swal.fire({
          title: 'Tidak Dibenarkan',
          html: `No Kad Pengenalan yang dimasukkan menunjukkan umur kurang daripada 17 tahun.<br><br>
             <strong>Pendaftaran hanya dibenarkan untuk pengguna berumur 17 tahun ke atas.</strong>`,
          icon: 'warning',
          confirmButtonText: 'OK',
        });
        return;
      }
      if (!this.isNonHrmisFormValid) {
        Swal.fire({
          title: 'Ralat',
          text: 'Sila isi semua maklumat yang diperlukan dengan betul',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }
      Swal.fire({
        title: 'Daftar Pengguna Awam',
        html: `Anda pasti untuk mendaftar pengguna awam berikut?<br><br>
      <strong>Nama:</strong> ${this.nonHrmisData.name}<br>
      <strong>No KP:</strong> ${this.nonHrmisData.nokp}<br>
      <strong>Emel:</strong> ${this.nonHrmisData.email}<br>
      <strong>Agensi:</strong> ${this.nonHrmisData.ministry.toUpperCase()}<br>
      <strong>Peranan:</strong> ${this.nonHrmisData.role === 'pengguna' ? 'Pengguna' : 'Pentadbir Awam'}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, Daftar',
        cancelButtonText: 'Batal',
      }).then(async (result) => {
        if (result.isConfirmed) {
          const payload = {
            name: this.nonHrmisData.name,
            nokp: this.nonHrmisData.nokp,
            email: this.nonHrmisData.email,
            ministry: this.nonHrmisData.ministry,
            buid: this.nonHrmisData.buid,
            role: this.nonHrmisData.role,
            phone: this.nonHrmisData.phone,
            source: 'nonhrmis'
          };
          try {
            const response = await axios.post(`${baseURL}/register_public_with_agency.php`, payload);
            if (response.data.success === 1) {
              let successMessage = response.data.message;
              if (response.data.email_sent === true) {
                successMessage += ' Emel pengesahan telah dihantar kepada pengguna.';
              } else {
                successMessage += ' Namun, emel pengesahan tidak dapat dihantar. Sila hubungi pengguna secara manual.';
              }
              Swal.fire({
                title: 'Berjaya!',
                html: successMessage,
                icon: 'success',
                confirmButtonText: 'OK',
              });
              this.resetTambahPengguna();
              this.$nextTick(() => {
                this.$bvModal.hide('modal-tambah-pengguna');
              });
              this.getProfile();
              if (!this.nonHrmisData.buid && response.data.buid) {
                this.agencyOptions.push({ value: response.data.buid, text: this.nonHrmisData.ministry });
              }
            } else {
              if (response.data.message && response.data.message.toLowerCase().includes('alamat emel ini telah didaftarkan')) {
                Swal.fire({
                  title: 'Ralat',
                  text: 'Alamat emel ini telah didaftarkan. Sila gunakan alamat emel lain.',
                  icon: 'warning',
                  confirmButtonText: 'OK',
                });
              } else {
                Swal.fire({
                  title: 'Ralat',
                  text: response.data.message || 'Gagal mendaftar pengguna. Sila cuba lagi.',
                  icon: 'error',
                  confirmButtonText: 'OK',
                });
              }
            }
          } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
              if (error.response.data.message.toLowerCase().includes('alamat emel ini telah didaftarkan')) {
                Swal.fire({
                  title: 'Ralat',
                  text: 'Alamat emel ini telah didaftarkan. Sila gunakan alamat emel lain.',
                  icon: 'warning',
                  confirmButtonText: 'OK',
                });
              } else {
                Swal.fire({
                  title: 'Ralat',
                  text: error.response.data.message || 'Gagal mendaftar pengguna. Sila cuba lagi.',
                  icon: 'error',
                  confirmButtonText: 'OK',
                });
              }
            } else {
              Swal.fire({
                title: 'Ralat Pelayan',
                text: 'Ralat pelayan semasa mendaftar pengguna awam. Sila cuba lagi atau hubungi pentadbir sistem.',
                icon: 'error',
                confirmButtonText: 'OK',
              });
            }
          }
        }
      });
    },
    async aktifkanPengguna() {
      Swal.fire({
        title: 'Aktifkan Pengguna',
        text: `Anda pasti untuk mengaktifkan semula pengguna ${this.carianPengguna.name}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, Aktifkan',
        cancelButtonText: 'Batal',
      }).then(async (result) => {
        if (result.isConfirmed) {
          const payload = {
            nokp: this.nokp,
            action: 'activate'
          };
          try {
            const response = await axios.post(`${baseURL}/aktifkan_pengguna.php`, payload);
            if (response.data.success) {
              Swal.fire({
                title: 'Berjaya!',
                text: response.data.message,
                icon: 'success',
                confirmButtonText: 'OK',
              });
              this.resetTambahPengguna();
              this.$nextTick(() => {
                this.$bvModal.hide('modal-tambah-pengguna');
              });
              this.getProfile();
            } else {
              Swal.fire({
                title: 'Ralat',
                text: response.data.message,
                icon: 'error',
                confirmButtonText: 'OK',
              });
            }
          } catch (error) {
            Swal.fire({
              title: 'Ralat',
              text: 'Ralat pelayan semasa mengaktifkan pengguna',
              icon: 'error',
              confirmButtonText: 'OK',
            });
          }
        }
      });
    },
    validateNokp() {
      this.searchNokp = this.searchNokp.replace(/[^0-9]/g, '').slice(0, 12);
    },
    handleSearch() {
      if (this.searchNokp.length !== 12) {
        Swal.fire({
          title: 'Ralat',
          text: 'Sila masukkan No Kad Pengenalan yang sah (12 digit dan hanya nombor sahaja)',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }
      this.isSearching = true;
      this.currentPage = 1;
    },
    resetSearch() {
      this.searchNokp = '';
      this.isSearching = false;
      this.currentPage = 1;
    },
    tukarMilikProjek(userId) {
      const user = this.listuser.find(u => u.user_id === userId);
      if (!user) return;

      // Pastikan pengguna wujud dan ada maklumat penting
      if (!user.name || !user.email || !user.icno) {
        Swal.fire({
          title: 'Maklumat Tidak Lengkap',
          text: 'Maklumat pengguna tidak lengkap. Sila kemaskini maklumat terlebih dahulu.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      Swal.fire({
        title: 'Amaran',
        html: `<strong>Sila pastikan maklumat pengguna telah didaftarkan sebelum ke paparan seterusnya.</strong><br><br>
           <small>Pengguna: <strong>${user.name}</strong> (No KP: ${user.icno})</small>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, Teruskan',
        cancelButtonText: 'Batal',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.$router.push({ path: `/user/tukar/${userId}` });
        }
      });
    },
    navigateToUser(userId) {
      this.$router.push({ path: `/user/view/${userId}` });
    },
    editUser(userId) {
      this.$router.push({ path: `/user/edit/${userId}` });
    },

    async cariPengguna() {
      if (!this.nokp || this.nokp.length !== 12) {
        Swal.fire({
          title: 'Ralat',
          text: 'Sila masukkan No Kad Pengenalan yang sah (12 digit dan hanya nombor sahaja)',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }
      if (!this.validateAgeFromNokp(this.nokp)) {
        Swal.fire({
          title: 'Tidak Dibenarkan',
          html: `Pengguna dengan No Kad Pengenalan ini berumur kurang daripada 17 tahun.<br><br>
             <strong>Hanya pengguna berumur 17 tahun ke atas dibenarkan mendaftar.</strong>`,
          icon: 'warning',
          confirmButtonText: 'OK',
        });
        this.carianPengguna = { name: 'Tidak Layak', message: 'Umur kurang daripada 17 tahun' };
        this.showPublicRegistration = false;
        this.showRegistrationForm = false;
        return;
      }

      const user = JSON.parse(localStorage.getItem('user'));
      const peranan = user ? user.peranan : null;
      const buidagensi = user ? user.buidagensi : null;
      const icNumber = user ? user.icNumber : null; // Retrieve icNumber from localStorage
      const userType = user ? user.userType : null; // Retrieve userType from localStorage
      const url = `${baseURL}/cari_pengguna.php?nokp=${this.nokp}&peranan=${encodeURIComponent(peranan)}&buidagensi=${encodeURIComponent(buidagensi)}`;

      try {
        const response = await axios.get(url);
        if (response.data.success) {
          this.carianPengguna = response.data.data;

          // Condition 1: Check if icNumber exists, status is "NOKP ada di kedua-dua API dan DB", and user_type is empty
          if (
            icNumber &&
            response.data.status === 'NOKP ada di kedua-dua API dan DB' &&
            this.carianPengguna.user_type === ''
          ) {
            this.carianPengguna = {
              name: 'Akses Ditolak',
              message: 'Maklumat pengguna tidak boleh dipaparkan kerana jenis pengguna tidak ditentukan.'
            };
            this.showPublicRegistration = false;
            this.showRegistrationForm = false;
          }
          // Condition 2: Check if icNumber exists, peranan is "pentadbir awam", user_type is "public", ada_record is 1, and agency_id !== buidagensi
          else if (
            icNumber &&
            peranan === 'pentadbir awam' &&
            userType === 'public' &&
            this.carianPengguna.ada_record === 1 &&
            this.carianPengguna.agency_id !== buidagensi
          ) {
            this.carianPengguna = {
              name: 'Akses Ditolak',
              message: 'Maklumat pengguna tidak boleh dipaparkan kerana pengguna ini dari agensi yang berbeza.'
            };
            this.showPublicRegistration = false;
            this.showRegistrationForm = false;
          }
          else {
            this.nameTambah = this.carianPengguna.name;
            this.emailTambah = this.carianPengguna.email;
            this.ministryTambah = this.carianPengguna.ministry;
            this.showPublicRegistration = false;
            this.showRegistrationForm = false;

            if (this.carianPengguna.name === 'Tiada Maklumat Pengguna') {
              this.showPublicRegistration = true;
              await this.fetchAgencies();
            }
          }
        } else {
          this.showPublicRegistration = true;
          this.carianPengguna = null;
          await this.fetchAgencies();
        }
      } catch (error) {
        this.showPublicRegistration = true;
        this.carianPengguna = null;
        await this.fetchAgencies();
      }
    },
    showUserNotFoundPopup() {
      Swal.fire({
        title: 'Makluman',
        text: 'Pengguna tidak dijumpai. Sila semak No Kad Pengenalan yang dimasukkan.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    },
    showDifferentMinistryPopup() {
      Swal.fire({
        title: 'Makluman',
        html: 'Pengguna tidak dijumpai dalam organisasi anda. <br><br> Hanya pengguna dari organisasi yang sama boleh ditambah.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    },
    togglePublicRegistration() {
      if (!this.isAdmin) {
        Swal.fire({
          title: 'Akses Ditolak',
          text: 'Hanya pentadbir sistem dan pentadbir organisasi yang boleh mendaftar pengguna organisasi.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
        return;
      }
      this.showRegistrationForm = !this.showRegistrationForm;
      if (this.nokp && this.nokp.length === 12) {
        this.nonHrmisData.nokp = this.nokp;
      }
      if (this.showRegistrationForm) {
        this.fetchAgencies();
      }
    },
    cancelPublicRegistration() {
      this.showRegistrationForm = false;
      this.resetNonHrmisData();
    },
    resetTambahPengguna() {
      this.nokp = '';
      this.carianPengguna = null;
      this.nameTambah = '';
      this.emailTambah = '';
      this.ministryTambah = '';
      this.showPublicRegistration = false;
      this.showRegistrationForm = false;
      this.agencyMode = 'existing';
      this.resetNonHrmisData();
    },
    resetNonHrmisData() {
      this.nonHrmisData = {
        name: '',
        nokp: '',
        email: '',
        ministry: '',
        buid: null,
        role: null,
        phone: ''
      };
      this.agencyOptions = [];
    },
    async getProfile() {
      this.loading = true;
      const user = JSON.parse(localStorage.getItem('user'));
      const peranan = user ? user.peranan : null;
      const buidagensi = user ? user.buidagensi : null;
      if (!peranan || !buidagensi) {
        this.loading = false;
        return;
      }
      const requestData = {
        request: {
          peranan,
          buidagensi,
        },
      };
      try {
        const response = await axios.post(`${baseURL}/papar_user.php`, requestData, {
          headers: { 'Content-Type': 'application/json' },
        });
        this.listuser = response.data;
        this.loading = false;
        this.currentPage = 1;
      } catch (error) {
        this.loading = false;
        Swal.fire({
          title: 'Ralat',
          text: 'Ralat semasa memuat turun senarai pengguna. Sila cuba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    },
    async tambahPengguna() {
      const payload = {
        name: this.carianPengguna.name,
        email: this.carianPengguna.email,
        ministry: this.carianPengguna.ministry,
        nokp: this.nokp,
        agency_id: this.carianPengguna.agency_id,
      };
      try {
        const response = await axios.post(`${baseURL}/tambah_pengguna.php`, payload);
        if (response.data.success == 1) {
          Swal.fire({
            title: 'Berjaya!',
            text: response.data.message,
            icon: 'success',
            confirmButtonText: 'OK',
          });
          this.resetTambahPengguna();
          this.$nextTick(() => {
            this.$bvModal.hide('modal-tambah-pengguna');
          });
          this.getProfile();
        } else {
          Swal.fire({
            title: 'Makluman',
            text: response.data.message,
            icon: 'warning',
            confirmButtonText: 'OK',
          });
        }
      } catch (error) {
        Swal.fire({
          title: 'Ralat',
          text: 'Ralat pelayan semasa menambah pengguna',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  },
  created() {
    this.getProfile();
  },
};
</script>
<style scoped>
.public-registration-section {
  background: linear-gradient(145deg, #f8f9fa, #e9ecef);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-top: 1rem;
}

.registration-form-section {
  background: #ffffff;
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid #e3e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.no-user-found {
  background: #f8f9fa;
  border-radius: 0.75rem;
  margin-bottom: 1rem;
}

.table-container {
  margin-top: 1.5rem;
}

.user-table {
  background: #fff;
  border: 1px solid #e3e7eb;
}

.user-table thead th {
  background: linear-gradient(145deg, #f8f9fa, #e9ecef);
  color: #2c3e50;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  border-bottom: 2px solid #d1d8dd;
  padding: 1rem;
}

.user-table tbody td {
  vertical-align: middle;
  font-size: 0.9rem;
  padding: 0.75rem;
}

.user-table tbody tr:hover {
  background: #f5f6f8;
}

.input-group-animated {
  position: relative;
  margin-bottom: 1.5rem;
}

.input-group-animated label {
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  pointer-events: none;
}

.input-group-animated .form-control-animated:not(:placeholder-shown)~label,
.input-group-animated .form-control-animated:focus~label {
  top: -0.5rem;
  left: 0.75rem;
  font-size: 0.75rem;
  color: #007bff;
  background: #fff;
  padding: 0 0.25rem;
}

.form-control-animated {
  border: 1px solid #d1d8dd;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: #fff;
  width: 100%;
}

.form-control-animated:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.2);
  background: #f8f9fa;
}

.form-control-animated:read-only {
  background: #e9ecef;
}

.loading-container {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.no-data-container {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.text-dark {
  color: #2c3e50 !important;
}

.text-muted {
  color: #6c757d !important;
}

.btn-outline-primary,
.btn-outline-warning,
.btn-outline-info,
.btn-outline-secondary {
  font-size: 0.9rem;
  padding: 0.5rem 1.2rem;
  border-width: 1px;
  transition: all 0.3s ease;
}

.btn-primary,
.btn-success {
  font-size: 0.9rem;
  padding: 0.6rem 1.5rem;
  background: linear-gradient(145deg, #007bff, #0056b3);
  border: none;
  transition: all 0.3s ease;
}

.btn-primary:hover,
.btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.rounded-pill {
  border-radius: 50rem !important;
}

h3 {
  font-size: 1.5rem;
}

h4 {
  font-size: 1.25rem;
}

p {
  font-size: 0.9rem;
}

.badge {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.search-section {
  background: linear-gradient(145deg, #f8f9fa, #e9ecef);
  padding: 1.5rem;
  border-radius: 0.75rem;
  margin-bottom: 1.5rem;
}

.user-info-section {
  background: #fff;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.user-details {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.detail-item {
  margin-bottom: 0.75rem;
}

.detail-label {
  display: block;
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.detail-value {
  display: block;
  font-size: 0.95rem;
  color: #2c3e50;
}

.action-buttons {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e3e7eb;
}

.no-user-found {
  background: linear-gradient(145deg, #f8f9fa, #e9ecef);
  border-radius: 0.75rem;
  margin-top: 1rem;
}

.rounded-left-pill {
  border-top-left-radius: 50rem !important;
  border-bottom-left-radius: 50rem !important;
}

.rounded-right-pill {
  border-top-right-radius: 50rem !important;
  border-bottom-right-radius: 50rem !important;
}

.modal-header {
  border-bottom: 1px solid #e3e7eb;
  padding: 1.25rem 1.5rem;
}

.modal-title {
  font-weight: 600;
  font-size: 1.25rem;
}

.pagination-info {
  font-size: 0.9rem;
  color: #6c757d;
}

.agency-toggle {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.agency-card {
  flex: 1;
  padding: 0.75rem;
  background: #f8f9fa;
  border: 1px solid #d1d8dd;
  border-radius: 0.5rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.3s ease;
}

.agency-card.active {
  background: linear-gradient(145deg, #007bff, #0056b3);
  color: #fff;
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

.agency-card:hover:not(.active) {
  background: #e9ecef;
  border-color: #b0b8c1;
}

.agency-input-wrapper {
  position: relative;
  max-width: 690px;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.btn-loading {
  position: relative;
  color: transparent;
}

.btn-loading:after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -10px;
  margin-top: -10px;
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.access-denied-alert {
  border-left: 4px solid #ffc107;
  background-color: #fffbf0;
}

.admin-only-badge {
  background-color: #dc3545;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  margin-left: 0.5rem;
}

.white-bg {
  background: #ffffff !important;
}

.white-bg:focus {
  background: #ffffff !important;
  border-color: #007bff;
  box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.2);
}

.white-bg:disabled {
  background: #ffffff !important;
  color: #495057 !important;
  opacity: 1 !important;
}

.disabled-white-bg {
  background-color: #ffffff !important;
  color: #495057 !important;
  opacity: 1 !important;
  cursor: not-allowed;
}

.disabled-white-bg:disabled {
  background-color: #ffffff !important;
  color: #495057 !important;
  opacity: 1 !important;
}

.disabled-white-bg option {
  background-color: #ffffff !important;
  color: #495057 !important;
}

.access-denied-section {
  background: linear-gradient(145deg, #fff5f5, #fed7d7);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-top: 1rem;
  border: 1px solid #feb2b2;
}

.access-denied {
  background: #fff;
  border-radius: 0.5rem;
  padding: 2rem;
}

.agency-input-wrapper {
  position: relative;
}

.agency-input-wrapper:has(select)::after {
  content: "▼";
  font-size: 0.8rem;
  color: #495057;
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.agency-input-wrapper select.form-control-animated {
  padding-right: 2.5rem;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.agency-input-wrapper input.form-control-animated {
  padding-right: 1rem;
}
</style>
